import { setCookie } from '../cookie-utils';
import i18next from 'i18next';
import i18nextInstance from '../i18next';
import { trackSearchEvent } from '../search-events';
import { recordAffinity, namedTagAttribute } from '../search-personalization';

/** Resolve the t() function with the namespaces this module needs loaded. */
async function getT() {
  const t = await i18nextInstance;
  await i18next.loadNamespaces(['general', 'algolia']);
  return t;
}

function isMobile() {
  return window.matchMedia('(max-width: 767px)').matches;
}

/* ------------------------------------------------------------------ *
 * Nike-style mobile filter bar + per-filter bottom sheet
 * ------------------------------------------------------------------ */

const ALL_TARGET = '__all__';

// Only the explicitly enabled, non-Rolex native watch catalogue opts in.
function isNativeWatchCatalogue(): boolean {
  return !!document.querySelector('main[data-watch-buying="collection"]')
    && !/(?:^|\/)rolex(?:[-/]|$)/i.test(window.location.pathname);
}

let nativeSheetRevision = 0;
let nativeSheetTrigger: HTMLElement | null = null;

function syncNativeSheetAccessibility(filters: HTMLElement, open: boolean) {
  filters.setAttribute('aria-hidden', String(!open));
  filters.querySelectorAll<HTMLElement>('select, input, textarea, button, a, iframe').forEach((control) => {
    control.setAttribute('tabindex', open ? '0' : '-1');
  });
  document.querySelectorAll('.js-editorial-filters').forEach((control) => {
    control.setAttribute('aria-expanded', String(open));
  });
}

// The sort-by section isn't a "filter" — exclude it from the active-count badges.
const COUNTLESS_SECTION_IDS = ['filters-sortBy'];

/** All filterable sections in DOM order: the two static sections + each accordion item. */
function getFilterSections(): HTMLElement[] {
  const root = document.getElementById('js-filters');
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>('.js-filter-section, .js-accordion-item')
  );
}

function sectionLabel(section: HTMLElement): string {
  return (
    section.dataset.pillLabel ||
    section.querySelector('.filters__filter-title')?.textContent?.trim() ||
    ''
  );
}

/**
 * Count active refinements within a section. We only count refinement-list / toggle
 * checkboxes — numeric-menu radios (location, price buckets) always have a default
 * "Any/All" option selected, which isn't a real active filter.
 */
function sectionCount(section: HTMLElement): number {
  if (section.id && COUNTLESS_SECTION_IDS.includes(section.id)) return 0;
  return section.querySelectorAll(
    '.ais-RefinementList-checkbox:checked, .ais-ToggleRefinement-checkbox:checked'
  ).length;
}

/**
 * Build (or rebuild) the mobile pill bar from the current filter sections.
 * Idempotent — safe to call again after instant-search.ts appends dynamic filters.
 */
async function buildFilterBar() {
  const bar = document.querySelector<HTMLElement>('.js-filters-bar');
  if (!bar) return;

  const sections = getFilterSections();
  if (!sections.length) return;

  const t = await getT();

  bar.innerHTML = '';

  // Leading "All filters" pill
  const allPill = document.createElement('button');
  allPill.type = 'button';
  allPill.className = 'filters-bar__pill filters-bar__pill--all js-filter-pill';
  allPill.dataset.target = ALL_TARGET;
  allPill.innerHTML =
    `<span class="filters-bar__pill-icon" aria-hidden="true"></span>` +
    `<span class="filters-bar__label">${t('general:allFilters')}</span>` +
    `<span class="filters-bar__badge" hidden></span>`;
  bar.appendChild(allPill);

  sections.forEach((section, i) => {
    const id = section.dataset.sectionId || `filter-section-${i}`;
    section.dataset.sectionId = id;
    const label = sectionLabel(section);
    if (!label) return;

    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'filters-bar__pill js-filter-pill';
    pill.dataset.target = id;
    pill.innerHTML =
      `<span class="filters-bar__label">${label}</span>` +
      `<span class="filters-bar__badge" hidden></span>`;
    bar.appendChild(pill);
  });

  bar.removeAttribute('aria-hidden');
  updateFilterCounts();
}

/**
 * On single-brand ("branded") pages — e.g. /collections/timepieces/omega — the Brand
 * filter auto-hides (one option), so open the Collection filter by default instead.
 * Only decidable after Algolia renders the brand facet, hence called with
 * openDefaultAccordions() on every re-sync.
 */
function markBrandedPageAccordions() {
  const sections = getFilterSections();
  const collection = sections.find((s) => s.dataset.sectionId === 'collection');
  if (!collection || collection.dataset.accordionOpen) return;

  const brand = sections.find((s) => s.dataset.sectionId === 'brand');
  // Branded page = the Brand filter rendered with exactly one option
  if (brand?.querySelectorAll('.ais-RefinementList-item').length === 1) {
    collection.dataset.accordionOpen = 'true';
  }
}

/**
 * Expand accordions tagged `data-accordion-open` (e.g. Brand, Gender) once, after their
 * InstantSearch panel has rendered (the toggle/body don't exist until the first search).
 */
function openDefaultAccordions() {
  markBrandedPageAccordions();
  document
    .querySelectorAll<HTMLElement>('.js-accordion-item[data-accordion-open]:not([data-auto-opened])')
    .forEach((item) => {
      const toggle = item.querySelector<HTMLElement>('.js-accordion-toggle');
      const panel =
        item.querySelector<HTMLElement>('.js-accordion-panel') ||
        item.querySelector<HTMLElement>('.ais-Panel-body');
      if (!toggle || !panel) return; // not rendered yet — retry on the next mutation

      toggle.classList.add('open');
      panel.classList.add('open');
      panel.style.maxHeight = 'none';
      panel.removeAttribute('aria-hidden');
      item.setAttribute('data-auto-opened', 'true');
    });
}

/**
 * A section is hidden when its InstantSearch panel auto-hides (<= 1 option, e.g. Brand on
 * a one-brand page). Be robust about *how* the panel marks itself hidden (class / attribute)
 * and also fall back to mirroring the panel's own rule: a refinement list with <= 1 option.
 */
function isSectionHidden(section: HTMLElement): boolean {
  if (section.querySelector('.ais-Panel--hidden, .ais-Panel[hidden]')) return true;

  // Mirror the panel's hidden() rule for refinement-list filters: <= 1 option → hide.
  // (Doesn't apply to toggles / numeric menus, which have no RefinementList items.)
  const list = section.querySelector('.ais-RefinementList');
  if (list) {
    return list.querySelectorAll('.ais-RefinementList-item').length <= 1;
  }

  return false;
}

/** Update each pill's visibility + active-count badge (and the "All" total). */
function updateFilterCounts() {
  const bar = document.querySelector<HTMLElement>('.js-filters-bar');
  if (!bar) return;

  const sections = getFilterSections();
  let total = 0;

  sections.forEach((section) => {
    const pill = bar.querySelector<HTMLElement>(
      `.js-filter-pill[data-target="${section.dataset.sectionId}"]`
    );
    if (!pill) return;

    // Hide the pill for filters that have auto-hidden (e.g. Brand on a one-brand page)
    pill.hidden = isSectionHidden(section);

    const count = sectionCount(section);
    total += count;
    setBadge(pill, count);
  });

  setBadge(bar.querySelector<HTMLElement>('.js-filter-pill--all'), total);
}

function setBadge(pill: HTMLElement | null, count: number) {
  if (!pill) return;
  const badge = pill.querySelector<HTMLElement>('.filters-bar__badge');
  if (!badge) return;
  if (count > 0) {
    badge.textContent = String(count);
    badge.hidden = false;
    pill.classList.add('has-count');
  } else {
    badge.textContent = '';
    badge.hidden = true;
    pill.classList.remove('has-count');
  }
}

/** Open the bottom sheet showing a single section, or all sections for the "All" pill. */
async function openSheet(target: string) {
  const filters = document.getElementById('js-filters');
  const backdrop = document.querySelector<HTMLElement>('.js-filter-backdrop');
  if (!filters) return;

  const nativeWatch = isNativeWatchCatalogue();
  const revision = nativeWatch ? ++nativeSheetRevision : 0;
  if (nativeWatch) {
    const focused = document.activeElement as HTMLElement | null;
    if (focused && !filters.contains(focused)) nativeSheetTrigger = focused;
    // Mobile initialization and old sidebar controls leave the panel hidden to
    // assistive technology. Sheet visibility and tab stops must open together.
    syncNativeSheetAccessibility(filters, true);
    backdrop?.removeAttribute('hidden');
    document.body.classList.add('filters-sheet-open');
  }
  const isAll = target === ALL_TARGET;
  filters.dataset.mode = 'sheet';
  filters.classList.add('is-sheet-open');
  filters.classList.toggle('is-all', isAll);

  // Mark the active section
  getFilterSections().forEach((section) => {
    section.classList.toggle('is-active-section', !isAll && section.dataset.sectionId === target);
  });

  // Sheet title
  const titleEl = filters.querySelector<HTMLElement>('.js-sheet-title');
  if (titleEl) {
    const t = await getT();
    // A close (or a newer open) while translations load must not reopen the overlay.
    if (nativeWatch && revision !== nativeSheetRevision) return;
    if (isAll) {
      titleEl.textContent = t('general:allFilters');
    } else {
      const section = getFilterSections().find((s) => s.dataset.sectionId === target);
      titleEl.textContent = `${t('algolia:filterBy')} ${section ? sectionLabel(section) : ''}`.trim();
    }
  }

  backdrop?.removeAttribute('hidden');
  document.body.classList.add('filters-sheet-open');

  // Sync active pill styling
  document.querySelectorAll('.js-filter-pill').forEach((p) => {
    p.classList.toggle('is-active', (p as HTMLElement).dataset.target === target);
    if (nativeWatch) p.setAttribute('aria-expanded', String((p as HTMLElement).dataset.target === target));
  });
}

function closeSheet() {
  const filters = document.getElementById('js-filters');
  const backdrop = document.querySelector<HTMLElement>('.js-filter-backdrop');
  if (!filters) return;

  if (isNativeWatchCatalogue()) {
    ++nativeSheetRevision;
    syncNativeSheetAccessibility(filters, false);
    document.querySelectorAll('.js-filter-pill').forEach((pill) => pill.setAttribute('aria-expanded', 'false'));
    filters.classList.remove('is-all', 'filters--open');
    filters.removeAttribute('data-mode');
    getFilterSections().forEach((section) => section.classList.remove('is-active-section'));
    document.querySelector('.collection')?.classList.remove('collection--filters-open');
    if (nativeSheetTrigger?.isConnected) nativeSheetTrigger.focus();
    nativeSheetTrigger = null;
  }

  // Legacy catalogues keep their mode/section during the close animation;
  // the native watch flow clears both sheet and sidebar state together above.
  filters.classList.remove('is-sheet-open');
  document.querySelectorAll('.js-filter-pill.is-active').forEach((p) => p.classList.remove('is-active'));

  backdrop?.setAttribute('hidden', '');
  document.body.classList.remove('filters-sheet-open');
}

function isSheetOpen() {
  return document.getElementById('js-filters')?.classList.contains('is-sheet-open');
}

/** Wire up the pill bar, bottom sheet, and live count updates. */
function initFilterBar() {
  const collectionMain = document.querySelector('.collection__main');
  if (!collectionMain) return;

  // (Re)build the bar now and whenever instant-search.ts adds dynamic filters
  buildFilterBar();
  document.addEventListener('accordion:add', () => {
    buildFilterBar();
    openDefaultAccordions();
  });

  // Open a sheet when a pill is clicked (delegated — pills are built dynamically)
  document.addEventListener('click', (e) => {
    const pill = (e.target as HTMLElement).closest<HTMLElement>('.js-filter-pill');
    if (pill && pill.dataset.target) {
      e.preventDefault();
      openSheet(pill.dataset.target);
    }
  });

  // Close sheet on ×, Apply (footer btn), or backdrop
  document.querySelector('.js-sheet-close')?.addEventListener('click', (e) => {
    e.preventDefault();
    closeSheet();
  });
  document.querySelector('.js-filter-backdrop')?.addEventListener('click', closeSheet);

  // Keep badge counts in sync: on any refinement change + on Algolia re-renders
  const filters = document.getElementById('js-filters');
  filters?.addEventListener('change', updateFilterCounts);

  // Personalization signal: record which facets shoppers actually filter by
  // (only when applying a filter, not clearing it) — see search-events.ts
  filters?.addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    if (!input?.classList?.contains('ais-RefinementList-checkbox') || !input.checked) return;
    const filterId = input.closest('.filters__checklist')?.id ?? null;
    trackSearchEvent('collection_filter_click', {
      filter_attribute: filterId,
      filter_value: input.value,
      surface: 'collection',
    });
    // Affinity profile feeds optionalFilters boosts (search-personalization.ts).
    // Allowlist of filter ids whose Algolia attribute follows the
    // named_tags.<LOCALE>_<id> convention and carries real preference signal.
    if (filterId && ['brand', 'category', 'collection'].includes(filterId)) {
      recordAffinity(namedTagAttribute(filterId), input.value);
    }
  });
  if (filters) {
    // Watch childList (Algolia re-renders) AND class changes (panels gaining
    // `ais-Panel--hidden` when a filter auto-hides, e.g. single-brand Brand).
    new MutationObserver(() => {
      updateFilterCounts();
      openDefaultAccordions();
    }).observe(filters, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  // Fallbacks: the panel hidden state is only known after Algolia's first render, which
  // can land before the observer is watching or before the pills exist. Re-sync a couple
  // of times so auto-hidden filters (e.g. single-brand Brand) can't get stuck visible.
  setTimeout(() => { updateFilterCounts(); openDefaultAccordions(); }, 800);
  setTimeout(() => { updateFilterCounts(); openDefaultAccordions(); }, 2000);
}

/**
 * Set the `filtersOpen` cookie that controls if the filters sidebar is open on page load.
 * Only sets the cookie if consented to preferences cookies.
 */
function saveFilterPreference(isOpen: boolean) {
  const consented = Cookiebot?.consent.preferences;
  if (!consented) {
    return;
  }

  setCookie('filtersOpen', isOpen, 365);
}

/**
 * Open filters, enable tabbing to elements inside filters, and focus on first element inside filters
 */
function openFilters() {
  const filters = document.getElementById('js-filters');
  const collectionBlock = document.querySelector('.collection');
  if (!filters || !collectionBlock) {
    return;
  }

  // Show the filters panel
  filters.classList.add('filters--open');
  filters.setAttribute('aria-hidden', 'false');
  collectionBlock.classList.add('collection--filters-open');

  // Set tabindex of filters panel clickable elements to 0
  const filterLinks = filters.querySelectorAll<HTMLElement>('select, input, textarea, button, a, iframe');
  filterLinks.forEach((link) => {
    link.setAttribute('tabindex', '0');
  });
  filterLinks[0].focus();

  // Set any filter toggle buttons to showing
  const filterToggleBtns = document.querySelectorAll('.js-filter-toggle');
  filterToggleBtns.forEach((btn) => {
    btn.setAttribute('data-showing', 'true');
  });

  /**
   * Make a custom event to trigger accordion binding.
   * I'm doing it here because the filters still aren't present in the DOM
   * until slightly after adding them in collections/instant-search.ts,
   * and this is less janky than waiting for a timeout.
   */
  const event = new CustomEvent('accordion:add');
  document.dispatchEvent(event);

  saveFilterPreference(true);
}

/**
 * Close filters, disable tabbing to elements inside filters, and focus on original element
 * @param origFocus The element that was focused before the modal opened
 */
function closeFilters(origFocus?: HTMLElement) {
  const filters = document.getElementById('js-filters');
  if (isNativeWatchCatalogue() && (isSheetOpen() || filters?.dataset.mode === 'sheet')) {
    closeSheet();
    return;
  }
  const collectionBlock = document.querySelector('.collection');
  if (!filters || !collectionBlock) {
    return;
  }

  // Hide the filters panel
  filters.classList.remove('filters--open');
  filters.setAttribute('aria-hidden', 'true');
  collectionBlock.classList.remove('collection--filters-open');

  // Set tabindex of filters panel elements back to -1
  let filterLinks = filters.querySelectorAll('select, input, textarea, button, a, iframe');
  filterLinks.forEach((link) => {
    link.setAttribute('tabindex', '-1');
  });

  // Move focus back to original focus before filters opened
  if (origFocus) {
    origFocus.focus();
  }

  // Reset any filter toggle buttons to not showing
  const filterToggleBtns = document.querySelectorAll('.js-filter-toggle');
  filterToggleBtns.forEach((btn) => {
    btn.setAttribute('data-showing', 'false');
  });

  // Emit modal close event
  // TODO: Do I need this anymore if it's just a slide-out panel?
  // filters.dispatchEvent(new CustomEvent('close', { bubbles: true }));

  saveFilterPreference(false);
}

// The actual event listener to hook it all up
document.addEventListener('DOMContentLoaded', () => {
  let origFocus;

  // Close filters on page load if on mobile
  // (This technically has a slight animation but it's not as noticeable
  // if loading at the top of the page and only happens once since it saves closed preference afterwards
  if (isMobile()) {
    closeFilters();
  }

  // toggle filters when clicking on toggle button
  const filterToggleBtns = document.querySelectorAll('.js-filter-toggle');
  filterToggleBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      origFocus = e.target as HTMLElement;

      const showing = btn.getAttribute('data-showing') === 'true';
      if (showing) {
        closeFilters(origFocus);
      } else {
        openFilters();
      }
    });
  });

  // open filters when clicking on button
  const filterOpenBtns = document.querySelectorAll('.js-filter-open');
  filterOpenBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      origFocus = e.target;

      openFilters();
    });
  });

  // close filters when clicking on close button (also doubles as the mobile sheet "Apply")
  const filterCloseBtns = document.querySelectorAll('.js-filter-close');
  filterCloseBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (isSheetOpen()) {
        closeSheet();
      } else {
        closeFilters(origFocus);
      }
    });
  });

  // Close filters / sheet when pressing ESC
  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (isSheetOpen()) {
        closeSheet();
      } else {
        closeFilters(origFocus);
      }
    }
  });

  // Nike-style mobile pill bar + per-filter bottom sheet
  initFilterBar();
});
